import { useGetStudentChurnScore, useResolveChurn } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import ChurnRiskBadge, {
  churnBandOf,
  churnBandTone,
} from '@components/V4/ChurnRisk/ChurnRiskBadge';
import { ChurnHistoryPointDTO, StudentChurnDTO } from '@shared/clientTypes/DTOs/ChurnDTO';
import { ENROLLMENT_STATUS_TYPES, FLAG_TYPES } from '@shared/constants';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useContext } from 'react';
import DetailsModalSection from '../../../DetailsModalSection';
import { StudentDetailsPageContext } from '../../StudentDetailsPage';

const Stat = (props: { label: string; value: React.ReactNode; hint?: string }) => (
  <div className="flex flex-col gap-1 rounded-2xl bg-surfaceObject p-4">
    <span className="text-xs text-customGrey">{props.label}</span>
    <span className="text-lg font-semibold leading-tight text-white">{props.value}</span>
    {props.hint ? <span className="text-xs text-customGrey">{props.hint}</span> : null}
  </div>
);

const Sparkline = (props: { points: ChurnHistoryPointDTO[] }) => {
  const { points } = props;
  if (points.length < 2) {
    return <span className="text-xs text-customGrey">Not enough history yet.</span>;
  }

  const width = 320;
  const height = 64;
  const first = moment(points[0].at).valueOf();
  const last = moment(points[points.length - 1].at).valueOf();
  const span = Math.max(1, last - first);
  const coordinates = points.map((point) => {
    const x = ((moment(point.at).valueOf() - first) / span) * (width - 8) + 4;
    const y = height - 4 - (Math.min(100, Math.max(0, point.score)) / 100) * (height - 8);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-16 w-full max-w-xs" aria-hidden>
      <line
        x1="4"
        x2={width - 4}
        y1={height - 4 - 0.6 * (height - 8)}
        y2={height - 4 - 0.6 * (height - 8)}
        className="stroke-customRed/40"
        strokeDasharray="4 4"
      />
      <line
        x1="4"
        x2={width - 4}
        y1={height - 4 - 0.3 * (height - 8)}
        y2={height - 4 - 0.3 * (height - 8)}
        className="stroke-customGold/40"
        strokeDasharray="4 4"
      />
      <polyline
        points={coordinates.join(' ')}
        fill="none"
        className="stroke-white"
        strokeWidth="2"
      />
    </svg>
  );
};

const relativeDay = (value?: string) => (value ? moment(value).fromNow() : 'never');

const DriverRow = (props: { driver: StudentChurnDTO['drivers'][number] }) => {
  const { driver } = props;
  const ratio = driver.maxPoints ? driver.points / driver.maxPoints : 0;

  return (
    <div className="flex flex-col gap-1.5 rounded-xl bg-surfaceObject px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-white">{driver.label}</span>
        <span className="text-sm font-semibold tabular-nums text-customLight">
          +{driver.points}
          <span className="text-xs font-normal text-customGrey"> / {driver.maxPoints}</span>
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surfaceHover">
        <div
          className={mergeClassNames(
            'h-full rounded-full',
            ratio >= 0.75 ? 'bg-customRed' : ratio >= 0.4 ? 'bg-customGold' : 'bg-customGreen'
          )}
          style={{ width: `${Math.max(6, ratio * 100)}%` }}
        />
      </div>
      <span className="text-xs text-customGrey">{driver.detail}</span>
    </div>
  );
};

const ChurnRiskTab = () => {
  const { student } = useContext(StudentDetailsPageContext);
  const toast = useToast();
  const { data, isLoading, refetch } = useGetStudentChurnScore(student?._id);
  const { mutate: resolveChurn, isLoading: isResolving } = useResolveChurn({
    onSuccess: () => {
      toast.success('Churn risk marked as handled.');
      refetch();
    },
    onError: () => toast.error('Could not mark the churn risk as handled.'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner className="h-6 w-6" />
      </div>
    );
  }

  if (!data) {
    return (
      <DetailsModalSection className="mt-6" sectionTitle="Churn risk">
        <p className="text-sm text-customGrey">
          No churn score yet. Scores are computed nightly for students with an active, suspended or
          waiting-for-payment enrollment.
        </p>
      </DetailsModalSection>
    );
  }

  const { signals } = data;
  const delta = data.delta30;

  return (
    <div className="mt-6 flex flex-col gap-6">
      <section className="flex flex-wrap items-start justify-between gap-4 rounded-2xl bg-surfaceBackground p-5">
        <div className="flex flex-col gap-2">
          <span className="text-xs uppercase tracking-wide text-customGrey">Churn risk</span>
          <div className="flex items-center gap-3">
            <span
              className={mergeClassNames(
                'text-4xl font-bold tabular-nums',
                churnBandTone(data.band)
              )}
            >
              {Math.round(data.score)}
            </span>
            <span className="text-sm text-customGrey">/ 100</span>
            <ChurnRiskBadge score={data.score} band={data.band} size="large" />
          </div>
          <span className="text-xs text-customGrey">
            Computed {moment(data.computedAt).fromNow()}
            {typeof delta === 'number' && delta !== 0 ? (
              <>
                {' · '}
                <span className={delta > 0 ? 'text-customRed' : 'text-customGreen'}>
                  {delta > 0 ? '+' : ''}
                  {Math.round(delta)} in the last 30 days
                </span>
              </>
            ) : null}
          </span>
          {data.resolved ? (
            <span className="text-xs text-customGrey">
              Marked as handled {moment(data.resolvedAt).fromNow()}. It reopens automatically if the
              risk climbs again or after 90 days.
            </span>
          ) : (
            <Button
              variant="secondary"
              size="normal"
              buttonText="Mark as handled"
              isLoading={isResolving}
              onClick={() => resolveChurn([data.churnScoreId], {})}
              className="mt-1 w-fit"
            />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-customGrey">Last 90 days</span>
          <Sparkline points={data.history} />
        </div>
      </section>

      <DetailsModalSection sectionTitle="Why this score">
        {data.drivers.length ? (
          <div className="flex flex-col gap-2">
            {data.drivers.map((driver) => (
              <DriverRow key={driver.key} driver={driver} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-customGrey">
            Nothing is pulling the score up: sessions are regular, something is scheduled, and no
            payment or satisfaction issue is open.
          </p>
        )}
      </DetailsModalSection>

      <DetailsModalSection sectionTitle="Signals">
        <div className="grid grid-cols-2 gap-3 tablet:grid-cols-3 laptop:grid-cols-4">
          <Stat
            label="Last session held"
            value={relativeDay(signals.lastHeldAt)}
            hint={`Usual gap ~${signals.expectedGapDays} days`}
          />
          <Stat label="Next session" value={relativeDay(signals.nextScheduledAt)} />
          <Stat
            label="Sessions, last 30 days"
            value={signals.heldLast30}
            hint={`${signals.heldPrev30} in the 30 days before`}
          />
          <Stat label="Missed or cancelled, 60 days" value={signals.missedLast60} />
          <Stat
            label="Mentor changes, 90 days"
            value={signals.mentorChangesLast90}
            hint={
              signals.lastMentorChangeAt
                ? `Latest ${relativeDay(signals.lastMentorChangeAt)}`
                : undefined
            }
          />
          <Stat
            label="Engagement (mentor rated)"
            value={signals.engagementAvg !== undefined ? `${signals.engagementAvg}/5` : '—'}
          />
          <Stat
            label="Parent rating"
            value={signals.parentRatingAvg !== undefined ? `${signals.parentRatingAvg}/5` : '—'}
            hint={
              signals.parentRatingCount
                ? `${signals.parentRatingCount} rated · latest ${
                    signals.parentRatingLatest ?? '—'
                  }/5`
                : 'Parent has not rated any session'
            }
          />
          <Stat
            label="Parent NPS"
            value={signals.parentNpsLast !== undefined ? `${signals.parentNpsLast}/10` : '—'}
          />
          <Stat
            label="Student NPS"
            value={signals.studentNpsLast !== undefined ? `${signals.studentNpsLast}/10` : '—'}
          />
          <Stat
            label="AI session quality"
            value={signals.sessionQualityAvg !== undefined ? `${signals.sessionQualityAvg}/5` : '—'}
            hint={
              signals.sessionQualityCount
                ? `last ${signals.sessionQualityCount} graded session${
                    signals.sessionQualityCount === 1 ? '' : 's'
                  }`
                : undefined
            }
          />
          <Stat
            label="Pace to graduation"
            value={signals.pace ? `${Math.round(signals.pace.actualRatio * 100)}% done` : '—'}
            hint={
              signals.pace
                ? `${signals.pace.completed} of ${signals.pace.total} sessions · ~${Math.round(
                    signals.pace.expectedRatio * 100
                  )}% expected by now · graduation ${moment(signals.pace.graduationAt).format(
                    'MMM YYYY'
                  )}`
                : 'No graduation year or too few sessions'
            }
          />
          <Stat
            label="Sessions left"
            value={`${signals.sessionsRemaining} / ${signals.sessionsTotal}`}
          />
          <Stat
            label="Payment"
            value={signals.overdueDays > 0 ? `${signals.overdueDays} days overdue` : 'OK'}
          />
          <Stat
            label="Enrollments"
            value={
              signals.enrollmentStatuses.length
                ? signals.enrollmentStatuses
                    .map((status) => ENROLLMENT_STATUS_TYPES[status])
                    .join(', ')
                : '—'
            }
          />
          <Stat
            label="Last login"
            value={
              signals.daysSinceLogin !== undefined ? `${signals.daysSinceLogin} days ago` : '—'
            }
          />
          {signals.hubspot ? (
            <>
              <Stat
                label="Support tickets"
                value={`${signals.hubspot.openTickets} open`}
                hint={`${signals.hubspot.ticketsLast90} in the last 90 days${
                  signals.hubspot.lastTicketSubject ? ` · ${signals.hubspot.lastTicketSubject}` : ''
                }`}
              />
              <Stat
                label="Team contact (HubSpot)"
                value={
                  signals.hubspot.lastTeamContactAt
                    ? moment(signals.hubspot.lastTeamContactAt).fromNow()
                    : 'never'
                }
                hint={`${signals.hubspot.callsLast90} calls · ${
                  signals.hubspot.emailsLast90
                } emails in 90 days${
                  signals.hubspot.unansweredCallsLast60
                    ? ` · ${signals.hubspot.unansweredCallsLast60} unanswered`
                    : ''
                }`}
              />
            </>
          ) : null}
        </div>
        {signals.activeFlags.length ? (
          <p className="mt-3 text-xs text-customGrey">
            Open flags: {signals.activeFlags.map((flag) => FLAG_TYPES[flag] ?? flag).join(', ')}
          </p>
        ) : null}
        <p className="mt-3 text-xs text-customGrey">
          Bands: low under 30, medium 30–59, high 60 and above.{' '}
          {churnBandOf(data.score) === 'high' ? 'This student needs a call this week.' : ''}
        </p>
      </DetailsModalSection>
    </div>
  );
};

export default ChurnRiskTab;
